import { ArrowLeft, Coins, History } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { Button } from '@/components/ui/button'
import { handleUnauthorized } from '@/lib/api-error-handler'
import { BACKEND_URL } from '@/lib/config'
import { getAuthToken } from '@/lib/supabase'

interface Transaction {
  id: string
  amount: number
  type: string
  description: string | null
  metadata: Record<string, unknown>
  created_at: string
}

interface CreditsHistoryPageProps {
  onClose: () => void
}

export function CreditsHistoryPage({ onClose }: CreditsHistoryPageProps) {
  const [transactions, setTransactions] = useState<Transaction[]>([])
  const [isLoadingTransactions, setIsLoadingTransactions] = useState(false)

  const fetchTransactions = useCallback(async () => {
    const token = await getAuthToken()
    if (!token) return

    setIsLoadingTransactions(true)
    try {
      const response = await fetch(`${BACKEND_URL}/api/billing/transactions`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'ngrok-skip-browser-warning': 'true',
        },
      })

      if (!response.ok) {
        if (response.status === 401) {
          await handleUnauthorized(response, 'Fetch transactions')
          return
        }
        const errorData = await response.json().catch(() => ({}))
        console.error('Failed to fetch transactions:', {
          status: response.status,
          statusText: response.statusText,
          error: errorData,
        })
        return
      }

      const data = await response.json()
      setTransactions(data.transactions || [])
    } catch (err) {
      console.error('Failed to fetch transactions:', err)
    } finally {
      setIsLoadingTransactions(false)
    }
  }, [])

  useEffect(() => {
    fetchTransactions()
  }, [fetchTransactions])

  return (
    <div className="flex flex-col h-full bg-background">
      {/* Header */}
      <div className="flex items-center gap-2 p-3 border-b">
        <Button variant="ghost" size="icon" onClick={onClose} className="size-8">
          <ArrowLeft className="size-4" />
        </Button>
        <div className="flex items-center gap-2">
          <Coins className="size-4" />
          <h1 className="text-sm font-semibold">Credits History</h1>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4">
        {isLoadingTransactions ? (
          <div className="flex items-center justify-center py-8">
            <p className="text-sm text-muted-foreground">Loading...</p>
          </div>
        ) : transactions.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 gap-2">
            <History className="size-12 text-muted-foreground/50" />
            <p className="text-sm text-muted-foreground">No credits history yet</p>
          </div>
        ) : (
          <div className="space-y-2">
            {transactions.map((tx) => {
              const isPositive = tx.amount > 0
              const date = new Date(tx.created_at)
              const formattedDate = date.toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year:
                  date.getFullYear() !== new Date().getFullYear()
                    ? 'numeric'
                    : undefined,
              })
              const formattedTime = date.toLocaleTimeString('en-US', {
                hour: 'numeric',
                minute: '2-digit',
              })

              return (
                <div
                  key={tx.id}
                  className="flex items-center justify-between gap-3 p-3 bg-muted/50 rounded-lg border border-border/50"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span
                        className={`text-base font-semibold ${
                          isPositive
                            ? 'text-green-600 dark:text-green-500'
                            : 'text-red-600 dark:text-red-500'
                        }`}
                      >
                        {isPositive ? '+' : ''}${tx.amount.toFixed(2)}
                      </span>
                      <span className="text-xs text-muted-foreground capitalize">
                        {tx.type.replace('_', ' ')}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <span>{formattedDate}</span>
                      <span>•</span>
                      <span>{formattedTime}</span>
                    </div>
                    {tx.description && (
                      <p className="text-xs text-muted-foreground mt-1">
                        {tx.description}
                      </p>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}
